import React, { Fragment } from 'react';
import { get } from 'lodash';

import t from '../../../lib/I18n';
import Checkbox from '../../form/Checkbox';
import ValidatedFormField from '../../form/ValidatedField';
import Radio from '../../form/Radio';
import { Button } from 'react-bootstrap';
import Icon from '../../general/Icon';
import { CONSENTS } from '../../../constants/AppConstants';

// optionId values come back from the consents API, not from this codebase — map them
// explicitly rather than deriving a kebab-case id from them mechanically (see 05-fe-followups.md #1).
const NOTIFICATION_METHOD_TEST_IDS = {
    [CONSENTS.EMAIL_MARKETING]: 'marketing-consents-email-checkbox',
    [CONSENTS.PUSH_MARKETING]: 'marketing-consents-push-checkbox',
    [CONSENTS.SMS_Marketing]: 'marketing-consents-sms-checkbox',
};
import { WithLoading } from '../../ui/WithLoading';
import { useConsents } from '../../../hooks/useConsents';
import ExternalLink from '../../general/ExternalLink';
import FormattedHTMLMessage from '../../general/FormattedHTMLMessage';

export const Consents = () => {
    const {
        consents,
        consentsForm,
        onCheckNotificationMethod,
        onChangePhoneNumber,
        onChangeIsOver16,
        handleSubmit,
        submitLoading,
        consentsLoading,
        showSubmitButton,
        phoneVisited,
        setPhoneVisited,
    } = useConsents();

    const notificationMethods = get(consents, 'blocks[0].options', []);
    const over16Title = get(consents, 'blocks[1].title', '');
    const over16Choices = get(consents, 'blocks[1].choices', []);
    const isOver16 = consentsForm.get(CONSENTS.OVER_16);
    const privacyPolicyURL = get(consents, 'privacyPolicy.policyUrl');

    return (
        <WithLoading isLoading={consentsLoading}>
            <div>
                <p>{t('preference-centre.consents.title')}</p>
                <p><strong>{t('preference-centre.consents.title_2')}</strong></p>
                <ul>
                    {notificationMethods.map((method) => {
                        const { optionId, label } = method;
                        return (
                            <li key={optionId}>
                                <Checkbox
                                    label={label}
                                    aria-label={label}
                                    data-test-id={NOTIFICATION_METHOD_TEST_IDS[optionId]}
                                    onChange={onCheckNotificationMethod(method)}
                                    checked={consentsForm.get(optionId)}
                                    value={optionId}
                                />
                            </li>
                        );
                    })}
                </ul>
                {consentsForm.get(CONSENTS.SMS_Marketing) && (
                    <Fragment>
                        <label htmlFor={CONSENTS.PHONE} className="label">
                            {t('preference-centre.consents.phoneNumber')}
                        </label>
                        <ValidatedFormField
                            type="text"
                            form={consentsForm}
                            id={CONSENTS.PHONE}
                            field={CONSENTS.PHONE}
                            data-test-id="marketing-consents-phone-input"
                            className="input-field margin-bottom--sm consents__input"
                            maxLength={21}
                            onChange={onChangePhoneNumber}
                            onBlur={() => setPhoneVisited(true)}
                            label={t('preference-centre.consents.phoneNumber')}
                            aria-label={t('preference-centre.consents.phoneNumber.sr')}
                            validation={consentsForm.getValidationSummary()}
                            visited={phoneVisited}
                            required
                        />
                    </Fragment>
                )}
            </div>
            <div>
                <p><strong>{over16Title}</strong></p>
                <ul>
                    <li>
                        <Radio
                            radioClass="regular-radio"
                            label={get(over16Choices, '[0].label', t('preference-centre.consents.over16'))}
                            aria-label={get(over16Choices, '[0].label', t('preference-centre.consents.over16'))}
                            data-test-id="marketing-consents-over-16-radio"
                            checked={isOver16}
                            onChange={onChangeIsOver16(true)}
                        />
                    </li>
                    <li>
                        <Radio
                            radioClass="regular-radio"
                            label={get(over16Choices, '[1].label', t('preference-centre.consents.under16'))}
                            aria-label={get(over16Choices, '[1].label', t('preference-centre.consents.under16'))}
                            data-test-id="marketing-consents-under-16-radio"
                            checked={!isOver16}
                            onChange={onChangeIsOver16(false)}
                        />
                    </li>
                </ul>
            </div>
            <p className="margin-bottom--md">
                <FormattedHTMLMessage
                    message='preference-centre.consents.privacyPolicy'
                    link={
                        <ExternalLink
                            message='preference-centre.consents.privacyPolicyLink'
                            href={ privacyPolicyURL }
                        />
                    }
                />
            </p>
            {showSubmitButton && (
                <Button
                    className="btn-blue"
                    disabled={
                        submitLoading ||
                        (consentsForm.get(CONSENTS.SMS_Marketing) && !!consentsForm.getError(CONSENTS.PHONE))
                    }
                    onClick={handleSubmit}
                >
                    {submitLoading ? (
                        <Icon icon="spinner" className="otrl-spin" />
                    ) : (
                        t('button.text.submit')
                    )}
                </Button>
            )}
        </WithLoading>
    );
};
